import { Component } from '@theme/component';

/**
 * Newsletter signup without a page jump. The stock customer form posts and
 * Shopify redirects back to the page (full reload, scroll to top, then the
 * anchor scrolls back down). This component submits the same form with
 * fetch, reads the server-rendered result out of the redirected page's HTML
 * and shows it inline.
 *
 * Result detection (in the parsed response):
 *   1. `#Email-signup__message-success-<block id>` exists  -> success, that
 *      markup is cloned into the `message` ref;
 *   2. otherwise `#Email-signup__message-error-<block id>` exists -> error,
 *      cloned the same way;
 *   3. otherwise any 2xx page -> success (Shopify accepted the POST; the URL
 *      normally carries `customer_posted=true`), rendered from the Liquid
 *      `successTemplate`;
 *   4. a redirect to /challenge (CAPTCHA), a network failure or an HTTP error
 *      -> the native submit runs, exactly like the no-JS path.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} input - The email input.
 * @property {HTMLButtonElement} button - The submit button.
 * @property {HTMLElement} message - Live region holding the result message.
 * @property {HTMLTemplateElement} successTemplate - Local fallback success markup.
 *
 * @extends {Component<Refs>}
 */
class EmailSignupFormComponent extends Component {
  // The <form> is NOT a ref on purpose: it is emitted by the Liquid `form` tag,
  // which does not reliably pass a `ref` attribute through, and a missing
  // required ref throws in connectedCallback — leaving the form on its native
  // submit (full page reload + jump to the footer).
  requiredRefs = ['input', 'button', 'message', 'successTemplate'];

  /** @type {AbortController | null} */
  #request = null;

  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    // `submit` bubbles from the form to this element, so the listener lives
    // here and survives any re-render of the form markup inside.
    this.addEventListener('submit', this.#submitWithoutNavigation, { signal: this.#listeners.signal });
  }

  /** @returns {HTMLFormElement} */
  get #form() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) throw new Error('email-signup-form-component: no <form> inside');
    return form;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    this.#setBusy(false);
  }

  /**
   * @returns {string}
   */
  get #blockId() {
    const blockId = this.dataset.blockId;
    if (!blockId) throw new Error('email-signup-form-component: data-block-id is missing');
    return blockId;
  }

  /**
   * @param {SubmitEvent} event
   */
  #submitWithoutNavigation = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    event.preventDefault();
    this.#submit();
  };

  async #submit() {
    const form = this.#form;

    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;

    this.#setBusy(true);

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        signal: controller.signal,
      });
      // A CAPTCHA challenge is the one case the browser must handle itself.
      if (new URL(response.url, window.location.href).pathname.startsWith('/challenge')) {
        throw new Error('Signup redirected to the CAPTCHA challenge');
      }
      if (!response.ok) throw new Error(`Signup request failed: ${response.status}`);

      const html = await response.text();
      this.#showResult(this.#readResult(html));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      // Network failure or an unreadable response: hand over to the native
      // submit so the visitor still subscribes (also covers CAPTCHA challenges).
      this.#setBusy(false);
      this.#request = null;
      form.submit();
      return;
    }

    if (this.#request === controller) this.#request = null;
    this.#setBusy(false);
  }

  /**
   * @param {string} html - The redirected page.
   * @returns {{ type: 'success' | 'error', element: Element }}
   */
  #readResult(html) {
    const documentFromResponse = new DOMParser().parseFromString(html, 'text/html');
    const blockId = this.#blockId;

    const success = documentFromResponse.getElementById(`Email-signup__message-success-${blockId}`);
    if (success) return { type: 'success', element: success };

    const error = documentFromResponse.getElementById(`Email-signup__message-error-${blockId}`);
    if (error) return { type: 'error', element: error };

    // Shopify answered the POST with a 2xx page that shows no error for this
    // block: the subscription went through (normally the URL also carries
    // `customer_posted=true`). Render the local success template instead of
    // falling back to a native submit, which would reload and jump the page.
    const fallback = this.refs.successTemplate.content.firstElementChild;
    if (!fallback) throw new Error('email-signup-form-component: successTemplate is empty');
    return { type: 'success', element: fallback };
  }

  /**
   * @param {{ type: 'success' | 'error', element: Element }} result
   */
  #showResult({ type, element }) {
    const { message, input } = this.refs;

    const messageElement = /** @type {HTMLElement} */ (document.importNode(element, true));
    message.replaceChildren(messageElement);

    input.removeAttribute('aria-invalid');
    input.setAttribute('aria-describedby', messageElement.id);

    if (type === 'success') {
      input.value = '';
    } else {
      input.setAttribute('aria-invalid', 'true');
    }

    // The message carries tabindex="-1"; focusing it reads it out without scrolling the page.
    messageElement.focus({ preventScroll: true });
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.refs.button.disabled = busy;
    this.classList.toggle('_loading', busy);
    if (busy) {
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('email-signup-form-component')) {
  customElements.define('email-signup-form-component', EmailSignupFormComponent);
}
